import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Avatar,
  InputAdornment,
  alpha
} from '@mui/material';
import { 
  AddCircle, 
  Delete, 
  Scale, 
  Kitchen, 
  AttachMoney, 
  Inventory, 
  Analytics,
  Close as CloseIcon,
  Category as CategoryIcon,
  Add as AddIcon
} from '@mui/icons-material';
import Product from "../../Services/FoodCost";

export const ProductFormDialog = ({ 
  initialFormData,
  stocks,
  onSubmit,
  editingProduct,
  handleInputChange,
  handleIngredientChange,
  addIngredient,
  removeIngredient,
  open,
  onClose
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const restaurantId = localStorage.getItem("restaurantId");

  // Fetch categories when dialog opens
  useEffect(() => {
    if (open && restaurantId) {
      fetchCategories();
    }
  }, [open, restaurantId]);

  const fetchCategories = async () => {
    try {
      const categories = await Product.getCategories(restaurantId);
      setAvailableCategories(categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__new__") {
      setIsNewCategory(true);
      handleInputChange({ target: { name: "category", value: "" } });
    } else {
      setIsNewCategory(false);
      handleInputChange({ target: { name: "category", value } });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{ 
        sx: { 
          borderRadius: '24px', 
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        } 
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 3,
        px: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -20,
          right: -20,
          width: 150,
          height: 150,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)',
            width: 48,
            height: 48
          }}>
            <Inventory />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              letterSpacing: 0.5,
              fontFamily: "'Inter', sans-serif",
              mb: 0.5
            }}>
              {editingProduct ? "Modifier le produit" : "Créer un nouveau produit"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {editingProduct ? "Modifiez les informations et la composition" : "Définissez les caractéristiques et les ingrédients"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
              transform: 'rotate(90deg)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <br></br>
      <DialogContent sx={{ 
        p: 5,
        background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
        position: 'relative',
      }}>
        <ProductForm 
          form={initialFormData}
          stocks={stocks}
          onSubmit={(formData) => {
            onSubmit(formData);
            onClose();
          }}
          editingProduct={editingProduct}
          isDialog
          handleInputChange={handleInputChange}
          handleIngredientChange={handleIngredientChange}
          addIngredient={addIngredient}
          removeIngredient={removeIngredient}
          availableCategories={availableCategories}
          handleCategoryChange={handleCategoryChange}
          isNewCategory={isNewCategory}
          setIsNewCategory={setIsNewCategory}
        />
      </DialogContent>
    </Dialog>
  );
};

const ProductForm = ({ 
  form,
  editingProduct,
  stocks,
  onSubmit,
  isDialog,
  handleInputChange,
  handleIngredientChange,
  addIngredient,
  removeIngredient,
  availableCategories = [],
  handleCategoryChange,
  isNewCategory,
  setIsNewCategory
}) => {
  const theme = useTheme();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  // Helper function to handle quantity changes with decimal support
  const handleQuantityChange = (index, value) => {
    // Allow empty string, numbers, and decimal point
    if (value === '' || value === '.' || /^\d*\.?\d*$/.test(value)) {
      handleIngredientChange(index, "quantity", value);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Product Details */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du produit"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Inventory sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '12px',
                    bgcolor: '#F5F7FA',
                    '&:hover': {
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prix de vente (DT)"
                name="soldPrice"
                type="number"
                value={form.soldPrice}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '12px',
                    bgcolor: '#F5F7FA',
                    '&:hover': {
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Category Section - NEW */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>
            <Chip 
              label="Catégorie" 
              icon={<CategoryIcon />}
              sx={{ 
                background: alpha('#667eea', 0.1), 
                px: 2,
                '& .MuiChip-icon': {
                  color: '#667eea'
                }
              }} 
            />
          </Divider>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Catégorie du produit</InputLabel>
                <Select
                  value={isNewCategory ? "__new__" : (form.category || "")}
                  onChange={handleCategoryChange}
                  label="Catégorie du produit"
                  sx={{
                    borderRadius: '12px',
                    bgcolor: '#F5F7FA',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Aucune catégorie</em>
                  </MenuItem>
                  {availableCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CategoryIcon sx={{ fontSize: 18, color: '#667eea' }} />
                        {category}
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem value="__new__" divider>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#667eea' }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        + Nouvelle catégorie
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Show text field when creating new category */}
            {isNewCategory && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom de la nouvelle catégorie"
                  name="category"
                  value={form.category || ""}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Entrées, Plats Principaux, Desserts..."
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '12px',
                      bgcolor: '#F5F7FA',
                      '&:hover': {
                        bgcolor: '#FFFFFF',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      }
                    }
                  }}
                  helperText="Entrez un nom pour la nouvelle catégorie"
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Ingredients Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Chip 
              label="Composition de la recette" 
              icon={<Analytics />}
              sx={{ 
                background: alpha('#667eea', 0.1), 
                px: 2,
                '& .MuiChip-icon': {
                  color: '#667eea'
                }
              }} 
            />
          </Divider>
          
          {form.ingredients.map((ingredient, index) => (
            <Paper 
              key={index} 
              sx={{ 
                p: 2,
                mb: 2,
                borderRadius: '12px',
                background: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                }
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ingrédient</InputLabel>
                    <Select
                      value={ingredient.stockName || ''}
                      onChange={(e) => handleIngredientChange(index, "stockName", e.target.value)}
                      label="Ingrédient"
                      sx={{
                        borderRadius: '8px',
                        bgcolor: '#F5F7FA',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          }
                        }
                      }}
                    >
                      {stocks.map((stock) => (
                        <MenuItem key={stock.id} value={stock.productName}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ 
                              width: 24, 
                              height: 24,
                              background: alpha('#667eea', 0.1) 
                            }}>
                              <Kitchen sx={{ fontSize: 14, color: '#667eea' }} />
                            </Avatar>
                            {stock.productName}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8} md={5}>
                  <TextField
                    fullWidth
                    label="Quantité"
                    type="text"
                    size="small"
                    value={ingredient.quantity || ''}
                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Scale sx={{ color: '#667eea', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '8px',
                        bgcolor: '#F5F7FA',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          }
                        }
                      }
                    }}
                    placeholder="0.000"
                  />
                </Grid>
                <Grid item xs={4} md={2} sx={{ textAlign: 'right' }}>
                  <IconButton 
                    onClick={() => removeIngredient(index)} 
                    sx={{
                      color: theme.palette.error.main,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            onClick={addIngredient}
            startIcon={<AddCircle />}
            variant="outlined"
            fullWidth
            sx={{ 
              mt: 1,
              py: 1.5,
              borderRadius: '8px',
              border: '2px dashed',
              borderColor: alpha('#667eea', 0.3),
              color: '#667eea',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: alpha('#667eea', 0.05),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Ajouter un ingrédient
          </Button>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            sx={{ 
              height: 48, 
              borderRadius: '8px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8, #6b46a1)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 12px rgba(102,126,234,0.3)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {editingProduct ? "Mettre à jour le produit" : "Enregistrer le produit"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};